import {
  collection,
  documentId,
  getDocs,
  limit as fbLimit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { runtimeDb as db, requireCustomerRuntime } from "../../firestoreStudio/runtime/runtimeDb";
import { parseFilterValue } from "./conditionQueryService";
import { estimateOperationCost } from "./costEstimatorService";
import { analyzeQuery, buildIndexesJson } from "./indexAdvisorService";

/**
 * Enterprise Query Engine — decides between Firestore server-side queries and
 * client-side refinement, builds and executes server queries with cursor
 * pagination, and provides the advanced client-only operators.
 *
 * It EXTENDS the existing Collection Explorer: it consumes the same filter
 * objects produced by ExplorerFilterBuilder ({ field, op, value, type }) and
 * reuses conditionQueryService / indexAdvisorService / costEstimatorService.
 */

/** Operators Firestore can evaluate natively (server-pushable). */
export const SERVER_OPERATORS = new Set([
  "==", "!=", ">", "<", ">=", "<=", "in", "not-in", "array-contains", "array-contains-any",
]);

/** Operators that can only be evaluated on downloaded documents. */
export const CLIENT_ONLY_OPERATORS = new Set([
  "contains", "startsWith", "endsWith", "is-null", "is-not-null", "regex", "soundex", "fuzzy",
]);

const RANGE_OPERATORS = new Set(["!=", "<", "<=", ">", ">="]);

/**
 * Analyze the current filters and produce a query plan. The plan classifies
 * each clause as server-pushable or client-only and decides the execution mode.
 */
export function planQuery({ collectionName, filters = [], sortField = "id", sortAsc = true, pageLimit = 100 }) {
  const active = (filters || []).filter((f) => f.field && f.op);
  const serverClauses = active.filter((f) => SERVER_OPERATORS.has(f.op));
  const clientClauses = active.filter((f) => !SERVER_OPERATORS.has(f.op));

  const rangeFields = [...new Set(serverClauses.filter((f) => RANGE_OPERATORS.has(f.op)).map((f) => f.field))];

  const warnings = [];
  if (rangeFields.length > 1) {
    warnings.push("Multiple inequality fields — Firestore may require them to be indexed together.");
  }

  const mode = serverClauses.length ? "server" : "client";

  return {
    mode,
    collectionName,
    serverClauses,
    clientClauses,
    rangeFields,
    sortField,
    sortAsc,
    pageLimit,
    warnings,
  };
}

/** Build the Firestore constraint list for a server plan. */
export function buildConstraints(plan, { withLimit = true } = {}) {
  const constraints = [];
  for (const f of plan.serverClauses) {
    constraints.push(where(f.field, f.op, parseFilterValue(f.value, f.op)));
  }

  // Firestore requires the first orderBy to be on an inequality field.
  const rangeField = plan.rangeFields[0];
  if (rangeField && rangeField !== plan.sortField) {
    constraints.push(orderBy(rangeField, "asc"));
  }
  const orderTarget = plan.sortField === "id" ? documentId() : plan.sortField;
  constraints.push(orderBy(orderTarget, plan.sortAsc ? "asc" : "desc"));

  if (withLimit) constraints.push(fbLimit(plan.pageLimit + 1));
  return constraints;
}

/** Human-readable Firestore query string for the Explain panel. */
export function buildQueryString(plan) {
  const parts = [`collection("${plan.collectionName}")`];
  for (const f of plan.serverClauses) {
    const val = JSON.stringify(parseFilterValue(f.value, f.op));
    parts.push(`.where("${f.field}", "${f.op}", ${val})`);
  }
  const rangeField = plan.rangeFields[0];
  if (rangeField && rangeField !== plan.sortField) parts.push(`.orderBy("${rangeField}", "asc")`);
  parts.push(`.orderBy("${plan.sortField}", "${plan.sortAsc ? "asc" : "desc"}")`);
  parts.push(`.limit(${plan.pageLimit})`);
  return parts.join("\n  ");
}

function extractMissingIndex(error, plan) {
  const msg = String(error?.message || "");
  const urlMatch = msg.match(/https?:\/\/[^\s)]+/);
  const analysis = analyzeQuery({
    collectionName: plan.collectionName,
    clauses: plan.serverClauses.map((c) => ({ field: c.field, op: c.op })),
    orderByField: plan.sortField === "id" ? "" : plan.sortField,
    orderByDir: plan.sortAsc ? "asc" : "desc",
  });
  return {
    required: true,
    consoleUrl: urlMatch ? urlMatch[0] : null,
    index: analysis.index,
    indexesJson: buildIndexesJson(analysis.index),
    queryString: buildQueryString(plan),
  };
}

/**
 * Execute a server-side Firestore query. Only downloads matching documents plus
 * one extra to detect further pages (cursor pagination). Returns rich metrics
 * for the Explain panel and detects missing composite indexes.
 */
export async function executeServerQuery({
  collectionName,
  filters = [],
  sortField = "id",
  sortAsc = true,
  pageLimit = 100,
  cursor = null,
}) {
  requireCustomerRuntime();
  const plan = planQuery({ collectionName, filters, sortField, sortAsc, pageLimit });
  const constraints = buildConstraints(plan);

  let q = query(collection(db, collectionName), ...constraints);
  if (cursor) q = query(q, startAfter(cursor));

  const t0 = performance.now();
  try {
    const snap = await getDocs(q);
    const queryTimeMs = Math.round(performance.now() - t0);

    let docs = snap.docs;
    const hasMore = docs.length > plan.pageLimit;
    if (hasMore) docs = docs.slice(0, plan.pageLimit);

    const rows = docs.map((d) => ({ id: d.id, data: d.data() }));
    const lastSnap = docs.length ? docs[docs.length - 1] : cursor;
    const docsReturned = rows.length;
    const estimatedReads = docsReturned || 1;

    return {
      ok: true,
      mode: "server",
      plan,
      rows,
      cursor: lastSnap,
      hasMore,
      missingIndex: null,
      error: null,
      metrics: {
        queryTimeMs,
        docsReturned,
        estimatedReads,
        estimatedCost: estimateOperationCost({ reads: estimatedReads }).estimatedTotalUsd,
        fromCache: snap.metadata?.fromCache ?? false,
      },
    };
  } catch (error) {
    const isMissingIndex = error?.code === "failed-precondition";
    return {
      ok: false,
      mode: "server",
      plan,
      rows: [],
      cursor: null,
      hasMore: false,
      missingIndex: isMissingIndex ? extractMissingIndex(error, plan) : null,
      error: { code: error?.code || "error", message: error?.message || String(error) },
      metrics: { queryTimeMs: Math.round(performance.now() - t0), docsReturned: 0, estimatedReads: 0, estimatedCost: 0 },
    };
  }
}

/* ------------------------------------------------------------------ */
/* Advanced client-side operators                                      */
/* ------------------------------------------------------------------ */

/** American Soundex code for phonetic matching. */
export function soundex(input) {
  const s = String(input || "").toUpperCase().replace(/[^A-Z]/g, "");
  if (!s) return "";
  const codes = { B: 1, F: 1, P: 1, V: 1, C: 2, G: 2, J: 2, K: 2, Q: 2, S: 2, X: 2, Z: 2, D: 3, T: 3, L: 4, M: 5, N: 5, R: 6 };
  const first = s[0];
  let prev = codes[first] || 0;
  let result = first;
  for (let i = 1; i < s.length && result.length < 4; i += 1) {
    const code = codes[s[i]] || 0;
    if (code !== 0 && code !== prev) result += code;
    if (s[i] !== "H" && s[i] !== "W") prev = code;
  }
  return (result + "000").slice(0, 4);
}

/** Levenshtein edit distance. */
export function levenshtein(a = "", b = "") {
  const s = String(a);
  const t = String(b);
  if (s === t) return 0;
  if (!s.length) return t.length;
  if (!t.length) return s.length;
  let prev = Array.from({ length: t.length + 1 }, (_, i) => i);
  for (let i = 0; i < s.length; i += 1) {
    const curr = [i + 1];
    for (let j = 0; j < t.length; j += 1) {
      const cost = s[i] === t[j] ? 0 : 1;
      curr[j + 1] = Math.min(curr[j] + 1, prev[j + 1] + 1, prev[j] + cost);
    }
    prev = curr;
  }
  return prev[t.length];
}

/** Similarity ratio 0..1 based on edit distance. */
export function fuzzyRatio(a = "", b = "") {
  const s = String(a);
  const t = String(b);
  const max = Math.max(s.length, t.length);
  if (max === 0) return 1;
  return 1 - levenshtein(s, t) / max;
}

export const CLIENT_SEARCH_MODES = [
  { id: "contains", label: "Contains" },
  { id: "startsWith", label: "Starts with" },
  { id: "endsWith", label: "Ends with" },
  { id: "regex", label: "Regex" },
  { id: "soundex", label: "Soundex (phonetic)" },
  { id: "fuzzy", label: "Fuzzy" },
];

export function matchAdvanced(value, mode, term, threshold = 0.7) {
  const raw = value == null ? "" : (typeof value === "object" ? JSON.stringify(value) : String(value));
  const v = raw.toLowerCase();
  const t = String(term || "").toLowerCase();
  if (!t) return true;
  switch (mode) {
    case "contains": return v.includes(t);
    case "startsWith": return v.startsWith(t);
    case "endsWith": return v.endsWith(t);
    case "regex":
      try { return new RegExp(term, "i").test(raw); } catch { return false; }
    case "soundex":
      return v.split(/\s+/).some((word) => soundex(word) === soundex(t));
    case "fuzzy":
      return v.includes(t) || fuzzyRatio(v, t) >= threshold;
    default:
      return v.includes(t);
  }
}

/**
 * Apply an advanced client-side search to loaded rows. `field` of "__all__"
 * searches every field plus the document id.
 */
export function applyAdvancedSearch(rows, { field = "__all__", mode = "contains", term = "", threshold = 0.7 } = {}) {
  if (!term) return rows;
  return (rows || []).filter((row) => {
    if (field && field !== "__all__") {
      return matchAdvanced(row?.data?.[field], mode, term, threshold);
    }
    const data = row?.data || {};
    if (matchAdvanced(row?.id, mode, term, threshold)) return true;
    return Object.keys(data).some((k) => matchAdvanced(data[k], mode, term, threshold));
  });
}
